import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { toast } from "sonner";
import { Copy, Trash2, UserPlus } from "lucide-react";
import { Avatar, Badge, Button, ConfirmDialog, Input } from "@/components/ui";
import { UpgradePrompt } from "@/components/billing";
import { useEntitlements } from "@/packages/features-billing";
import { useTranslation } from "@/packages/utils";
import { SettingsSection } from "./SettingsSection";

// ── Types ──────────────────────────────────────────────────────────

type MemberRole = "owner" | "admin" | "member";
type InviteRole = "admin" | "member";

interface MemberManagementProps {
  workspaceId: Id<"workspaces">;
  currentUserRole: string;
}

// ── InviteForm ─────────────────────────────────────────────────────

function InviteForm({
  workspaceId,
  memberCount,
}: {
  workspaceId: Id<"workspaces">;
  memberCount: number;
}) {
  const { t } = useTranslation("settings");
  const { canInviteMembers } = useEntitlements();
  const createInvite = useMutation(api.workspace_invites.create);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("member");
  const [sending, setSending] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  async function handleSubmit() {
    if (!email.trim()) return;
    setSending(true);
    try {
      const result = await createInvite({
        workspaceId,
        email: email.trim(),
        role,
      });
      const link = `${window.location.origin}/invite/${result.token}`;
      setInviteLink(link);
      const invitedEmail = email.trim();
      setEmail("");
      toast.success(t("workspace.invite.sent", { email: invitedEmail }));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("workspace.invite.sendFailed"),
      );
    } finally {
      setSending(false);
    }
  }

  function handleCopyLink() {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    toast.success(t("workspace.invite.linkCopied"));
  }

  const selectClassName =
    "h-9 w-full rounded-button border border-border bg-card px-2.5 text-[14px] text-primary outline-none cursor-pointer hover:bg-card-hover transition-colors";

  return (
    <div className="mb-5 space-y-3">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <span className="block text-[13px] font-medium text-primary mb-1.5">
            {t("workspace.invite.emailLabel")}
          </span>
          <Input
            placeholder={t("workspace.invite.emailPlaceholder")}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setInviteLink(null);
            }}
          />
        </div>

        <label className="w-[140px] block">
          <span className="block text-[13px] font-medium text-primary mb-1.5">
            {t("workspace.invite.roleLabel")}
          </span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as InviteRole)}
            className={selectClassName}
          >
            <option value="member">{t("workspace.roles.member")}</option>
            <option value="admin">{t("workspace.roles.admin")}</option>
          </select>
        </label>

        <UpgradePrompt
          feature="additional members"
          requiredTier="professional"
          enabled={!canInviteMembers(memberCount)}
        >
          <Button
            variant="primary"
            icon={UserPlus}
            onClick={handleSubmit}
            disabled={!email.trim()}
            loading={sending}
          >
            {t("workspace.invite.submit")}
          </Button>
        </UpgradePrompt>
      </div>

      {inviteLink && (
        <div className="flex items-center gap-2 p-3 bg-page border border-border rounded-card">
          <input
            type="text"
            readOnly
            value={inviteLink}
            className="flex-1 bg-transparent text-[13px] text-primary outline-none"
          />
          <button
            type="button"
            onClick={handleCopyLink}
            className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
            title={t("workspace.invite.copyLink")}
          >
            <Copy size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── MembersTable ───────────────────────────────────────────────────

function MembersTable({
  members,
  canManage,
  isOwner,
  onRemove,
  onRoleChange,
}: {
  members: Array<{
    _id: Id<"workspaceMemberships">;
    userId: Id<"users">;
    userName: string;
    userEmail: string;
    avatarColor: string;
    imageUrl?: string;
    role: MemberRole;
    joinedAt: number;
  }>;
  canManage: boolean;
  isOwner: boolean;
  onRemove: (userId: Id<"users">) => void;
  onRoleChange: (userId: Id<"users">, role: "admin" | "member") => void;
}) {
  const { t } = useTranslation("settings");
  const [confirmRemove, setConfirmRemove] = useState<{
    userId: Id<"users">;
    name: string;
  } | null>(null);
  const [removing, setRemoving] = useState(false);

  async function handleConfirmRemove() {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      onRemove(confirmRemove.userId);
      toast.success(
        t("workspace.members.removed", { name: confirmRemove.name }),
      );
      setConfirmRemove(null);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("workspace.members.removeFailed"),
      );
    } finally {
      setRemoving(false);
    }
  }

  function formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  return (
    <>
      <div className="border border-border rounded-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-page">
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                {t("workspace.members.tableHeaders.member")}
              </th>
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                {t("workspace.members.tableHeaders.role")}
              </th>
              <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                {t("workspace.members.tableHeaders.joined")}
              </th>
              {canManage && (
                <th className="text-right text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5 w-[60px]">
                  {t("workspace.members.tableHeaders.actions")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr
                key={member._id}
                className="border-b border-border last:border-b-0 hover:bg-card-hover/50 transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={member.userName}
                      avatarUrl={member.imageUrl}
                      avatarColor={member.avatarColor}
                      size="sm"
                    />
                    <div>
                      <p className="text-[13px] font-medium text-primary">
                        {member.userName}
                      </p>
                      <p className="text-[12px] text-secondary">
                        {member.userEmail}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {member.role === "owner" || !isOwner ? (
                    <span className="text-[13px] font-medium text-primary capitalize">
                      {t(`workspace.roles.${member.role}`, {
                        defaultValue: member.role,
                      })}
                    </span>
                  ) : (
                    <select
                      value={member.role}
                      onChange={(e) =>
                        onRoleChange(
                          member.userId,
                          e.target.value as "admin" | "member",
                        )
                      }
                      className="text-[13px] text-primary bg-transparent border border-border rounded-button px-2 py-1 outline-none cursor-pointer hover:bg-card-hover transition-colors"
                    >
                      <option value="admin">
                        {t("workspace.roles.admin")}
                      </option>
                      <option value="member">
                        {t("workspace.roles.member")}
                      </option>
                    </select>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-[13px] text-secondary">
                    {formatDate(member.joinedAt)}
                  </span>
                </td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    {member.role !== "owner" && (
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmRemove({
                            userId: member.userId,
                            name: member.userName,
                          })
                        }
                        className="p-1.5 rounded-md text-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title={t("workspace.members.removeDialog")}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmRemove !== null}
        onClose={() => setConfirmRemove(null)}
      >
        <ConfirmDialog.Title>
          {t("workspace.members.removeDialog")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          {t("workspace.members.removeConfirm", { name: confirmRemove?.name })}
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleConfirmRemove}
          onCancel={() => setConfirmRemove(null)}
          confirmLabel={t("workspace.members.removeButton")}
          variant="danger"
          loading={removing}
        />
      </ConfirmDialog>
    </>
  );
}

// ── PendingInvitesTable ────────────────────────────────────────────

function PendingInvitesTable({
  invites,
  canManage,
  onRevoke,
}: {
  invites: Array<{
    _id: Id<"workspaceInvites">;
    email: string;
    role: string;
    status: string;
    token: string;
    invitedByName: string;
    createdAt: number;
    expiresAt: number;
  }>;
  canManage: boolean;
  onRevoke: (inviteId: Id<"workspaceInvites">) => void;
}) {
  const { t } = useTranslation("settings");
  const [confirmRevoke, setConfirmRevoke] = useState<{
    inviteId: Id<"workspaceInvites">;
    email: string;
  } | null>(null);
  const [revoking, setRevoking] = useState(false);

  const pendingInvites = invites.filter((inv) => inv.status === "pending");

  if (pendingInvites.length === 0) return null;

  function daysRemaining(expiresAt: number): string {
    const days = Math.ceil((expiresAt - Date.now()) / 86_400_000);
    if (days <= 0) return t("workspace.pendingInvites.expired");
    if (days === 1) return t("workspace.pendingInvites.oneDayLeft");
    return t("workspace.pendingInvites.daysLeft", { days });
  }

  function handleCopyInviteLink(token: string) {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success(t("workspace.invite.linkCopied"));
  }

  async function handleConfirmRevoke() {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      onRevoke(confirmRevoke.inviteId);
      toast.success(
        t("workspace.pendingInvites.revokeSuccess", {
          email: confirmRevoke.email,
        }),
      );
      setConfirmRevoke(null);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("workspace.pendingInvites.revokeFailed"),
      );
    } finally {
      setRevoking(false);
    }
  }

  return (
    <>
      <div className="mt-6">
        <h3 className="text-[14px] font-medium text-primary mb-3">
          {t("workspace.pendingInvites.title", {
            count: pendingInvites.length,
          })}
        </h3>
        <div className="border border-border rounded-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-page">
                <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                  {t("workspace.pendingInvites.tableHeaders.email")}
                </th>
                <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                  {t("workspace.members.tableHeaders.role")}
                </th>
                <th className="text-left text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5">
                  {t("workspace.pendingInvites.tableHeaders.expires")}
                </th>
                {canManage && (
                  <th className="text-right text-[11px] font-medium text-muted uppercase tracking-wider px-4 py-2.5 w-[120px]">
                    {t("workspace.pendingInvites.tableHeaders.actions")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {pendingInvites.map((invite) => (
                <tr
                  key={invite._id}
                  className="border-b border-border last:border-b-0 hover:bg-card-hover/50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-[13px] font-medium text-primary">
                        {invite.email}
                      </p>
                      <p className="text-[11px] text-muted">
                        {t("workspace.pendingInvites.invitedBy", {
                          name: invite.invitedByName,
                        })}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      color={{
                        bg: "rgba(99,102,241,0.12)",
                        text: "#6366f1",
                      }}
                    >
                      {invite.role.charAt(0).toUpperCase() +
                        invite.role.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[13px] text-secondary">
                      {daysRemaining(invite.expiresAt)}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleCopyInviteLink(invite.token)}
                          className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
                          title={t("workspace.invite.copyLink")}
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setConfirmRevoke({
                              inviteId: invite._id,
                              email: invite.email,
                            })
                          }
                          className="p-1.5 rounded-md text-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          title={t("workspace.pendingInvites.revokeTitle")}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRevoke !== null}
        onClose={() => setConfirmRevoke(null)}
      >
        <ConfirmDialog.Title>
          {t("workspace.pendingInvites.revokeDialog")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          {t("workspace.pendingInvites.revokeConfirm", {
            email: confirmRevoke?.email,
          })}
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleConfirmRevoke}
          onCancel={() => setConfirmRevoke(null)}
          confirmLabel={t("workspace.pendingInvites.revokeButton")}
          variant="danger"
          loading={revoking}
        />
      </ConfirmDialog>
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────

export function MemberManagement({
  workspaceId,
  currentUserRole,
}: MemberManagementProps) {
  const { t } = useTranslation("settings");
  const members = useQuery(api.workspaces.getMembers, { workspaceId });
  const canManage = currentUserRole === "owner" || currentUserRole === "admin";

  // Only fetch invites if user has permission (owner/admin)
  const invites = useQuery(
    api.workspace_invites.listByWorkspace,
    canManage ? { workspaceId } : "skip",
  );

  const removeMember = useMutation(api.workspaces.removeMember);
  const updateRole = useMutation(api.workspaces.updateMemberRole);
  const revokeInvite = useMutation(api.workspace_invites.revoke);

  const isOwner = currentUserRole === "owner";

  return (
    <SettingsSection
      title={`${t("workspace.members.label")} (${members?.length ?? 0})`}
      description={t("workspace.members.description")}
    >
      {canManage && (
        <InviteForm
          workspaceId={workspaceId}
          memberCount={members?.length ?? 0}
        />
      )}

      {members && members.length > 1 && (
        <MembersTable
          members={members}
          canManage={canManage}
          isOwner={isOwner}
          onRemove={(userId) => removeMember({ workspaceId, userId })}
          onRoleChange={(userId, role) =>
            updateRole({ workspaceId, userId, role })
          }
        />
      )}

      {invites && invites.length > 0 && (
        <PendingInvitesTable
          invites={invites}
          canManage={canManage}
          onRevoke={(inviteId) => revokeInvite({ inviteId, workspaceId })}
        />
      )}
    </SettingsSection>
  );
}
